import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PageHeader from '../../../components/PageHeader/PageHeader';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';
import CoreStaffService from '../../../services/CoreStaffService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { getIcon } from '../../../utils/IconHandler';
import StaffFormModal from './StaffFormModal';
import StaffConfirmDialog from './StaffConfirmDialog';
import StaffAuditDialog from './StaffAuditDialog';
import { fullName, staffErrorText, staffStatus } from './staffText';

const iconButtonSx = (color = 'grey.600', hover = 'primary.main') => ({
  width: 34,
  height: 34,
  minWidth: 34,
  p: 0,
  borderRadius: '50% !important',
  color,
  '&:hover': { backgroundColor: hover, color: '#fff' },
});

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('es-UY');
};

/** Personal de Axium: todas las cuentas tienen el mismo acceso al panel. */
const StaffPage = () => {
  const navigate = useNavigate();
  const myEmail = (localStorage.getItem('userEmail') || '').trim().toLowerCase();

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind, person }
  const [auditOpen, setAuditOpen] = useState(false);
  const [devLink, setDevLink] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await CoreStaffService.list();
      setStaff(Array.isArray(data) ? data : []);
    } catch (err) {
      setLoadError(staffErrorText(err, 'No se pudo cargar el personal.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return staff.filter((person) => !term || fullName(person).toLowerCase().includes(term) || person.email.includes(term));
  }, [staff, search]);

  // El mail sale por SMTP; en local no hay SMTP y el backend devuelve el link para poder probar el flujo.
  const reportEmail = (result, sentMessage) => {
    if (result?.devSetupLink) setDevLink(result.devSetupLink);
    if (result?.emailSent) SnackbarUtils.success(sentMessage);
    else if (!result?.devSetupLink) SnackbarUtils.warning('La cuenta quedó guardada pero el mail no salió. Usá "Reenviar" para intentarlo de nuevo.');
  };

  const handleSubmitForm = async (values) => {
    if (editing) {
      const wasMe = editing.email === myEmail;
      const result = await CoreStaffService.update(editing.id, values);
      setFormOpen(false);
      setEditing(null);
      SnackbarUtils.success('Datos guardados');
      if (wasMe && result?.staff?.email !== myEmail) {
        // Cambiar el correo cierra las sesiones de la cuenta, también esta.
        SnackbarUtils.info('Cambiaste tu correo: volvé a ingresar.');
        navigate('/logout');
        return;
      }
      if (result?.devSetupLink) setDevLink(result.devSetupLink);
    } else {
      const result = await CoreStaffService.create(values);
      setFormOpen(false);
      reportEmail(result, 'Cuenta creada. Le enviamos el mail para que defina su contraseña.');
    }
    await load();
  };

  const runConfirmed = async () => {
    const { kind, person } = confirm;
    if (kind === 'deactivate') {
      await CoreStaffService.deactivate(person.id);
      SnackbarUtils.success('Cuenta desactivada');
    } else if (kind === 'reactivate') {
      await CoreStaffService.reactivate(person.id);
      SnackbarUtils.success('Cuenta reactivada');
    } else if (kind === 'reset') {
      const result = await CoreStaffService.sendPasswordReset(person.id);
      reportEmail(result, person.passwordPending ? 'Invitación reenviada.' : 'Mail de restablecimiento enviado.');
    } else if (kind === 'delete') {
      await CoreStaffService.remove(person.id);
      SnackbarUtils.success('Cuenta eliminada');
    }
    setConfirm(null);
    await load();
  };

  const confirmTexts = (kind, person) => {
    const name = fullName(person) || person.email;
    switch (kind) {
      case 'deactivate':
        return { title: 'Desactivar cuenta', color: 'warning', confirmLabel: 'Desactivar', message: `${name} no va a poder ingresar y se cierran sus sesiones. Podés reactivarla cuando quieras.` };
      case 'reactivate':
        return { title: 'Reactivar cuenta', color: 'primary', confirmLabel: 'Reactivar', message: `${name} vuelve a poder ingresar con su contraseña actual.` };
      case 'reset':
        return person.passwordPending
          ? { title: 'Reenviar invitación', color: 'primary', confirmLabel: 'Reenviar', message: `Le enviamos a ${person.email} un mail nuevo con el link para definir su contraseña. El link anterior deja de servir.` }
          : { title: 'Restablecer contraseña', color: 'warning', confirmLabel: 'Enviar mail', message: `Le enviamos a ${person.email} un link (vale 1 hora) para elegir una contraseña nueva. Se cierran sus sesiones ahora mismo.` };
      default:
        return { title: 'Eliminar cuenta', color: 'error', confirmLabel: 'Eliminar', message: `Se elimina la cuenta de ${name} (${person.email}). Queda el registro en la auditoría, pero la cuenta no se puede recuperar: habría que crearla de nuevo.` };
    }
  };

  const columns = [
    {
      field: 'name', headerName: 'Nombre', flex: 1.4, minWidth: 160, valueGetter: (_value, row) => fullName(row),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">{params.value}</Typography>
          {params.row.email === myEmail && <Chip size="small" label="Vos" sx={{ ml: 1 }} />}
        </Box>
      ),
    },
    { field: 'email', headerName: 'Correo', flex: 1.6, minWidth: 200 },
    {
      field: 'status', headerName: 'Estado', flex: 0.8, minWidth: 120, sortable: false,
      renderCell: (params) => {
        const status = staffStatus(params.row);
        return <Chip size="small" color={status.color} label={status.label} />;
      },
    },
    { field: 'createdAt', headerName: 'Alta', flex: 0.7, minWidth: 110, valueFormatter: (value) => formatDate(value) },
    {
      field: 'actions', headerName: 'Acciones', flex: 1.3, minWidth: 190, sortable: false, align: 'center', headerAlign: 'center',
      renderCell: (params) => {
        const person = params.row;
        const isMe = person.email === myEmail;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
            <Tooltip title="Editar datos">
              <IconButton size="small" aria-label={`Editar ${person.email}`} onClick={() => { setEditing(person); setFormOpen(true); }} sx={iconButtonSx()}>
                {getIcon('edit')}
              </IconButton>
            </Tooltip>
            {person.active && (
              <Tooltip title={person.passwordPending ? 'Reenviar invitación' : 'Restablecer contraseña'}>
                <IconButton size="small" aria-label={`${person.passwordPending ? 'Reenviar invitación a' : 'Restablecer contraseña de'} ${person.email}`} onClick={() => setConfirm({ kind: 'reset', person })} sx={iconButtonSx()}>
                  {getIcon('mail')}
                </IconButton>
              </Tooltip>
            )}
            {!isMe && (
              <Tooltip title={person.active ? 'Desactivar' : 'Reactivar'}>
                <IconButton size="small" aria-label={`${person.active ? 'Desactivar' : 'Reactivar'} ${person.email}`} onClick={() => setConfirm({ kind: person.active ? 'deactivate' : 'reactivate', person })} sx={iconButtonSx()}>
                  {getIcon(person.active ? 'pause' : 'play')}
                </IconButton>
              </Tooltip>
            )}
            {!isMe && (
              <Tooltip title="Eliminar">
                <IconButton size="small" aria-label={`Eliminar ${person.email}`} onClick={() => setConfirm({ kind: 'delete', person })} sx={iconButtonSx('error.main', 'error.main')}>
                  {getIcon('trash')}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const texts = confirm ? confirmTexts(confirm.kind, confirm.person) : {};

  return (
    <Box p={3}>
      <PageHeader title="Personal" subtitle="Las personas que administran Axium. Todas tienen el mismo acceso." />
      <Paper sx={{ padding: 2, mt: 3 }}>
        {devLink && (
          <Alert severity="info" onClose={() => setDevLink(null)} sx={{ mb: 2, wordBreak: 'break-all' }}>
            Entorno local: no hay envío de mails. Link para definir la contraseña: <a href={devLink}>{devLink}</a>
          </Alert>
        )}
        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField label="Buscar" size="small" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: 300 }} placeholder="Nombre o correo" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableRefreshButton loading={loading} onClick={load} />
            <Button variant="outlined" onClick={() => setAuditOpen(true)}>Auditoría</Button>
            <Button variant="contained" startIcon={getIcon('add')} onClick={() => { setEditing(null); setFormOpen(true); }}>Nueva persona</Button>
          </Box>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 820 }}>
            <DataGrid
              loading={loading}
              rows={rows}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              localeText={{ noRowsLabel: 'No hay personas para mostrar' }}
            />
          </Box>
        </Box>
      </Paper>

      <StaffFormModal open={formOpen} staff={editing} onClose={() => { setFormOpen(false); setEditing(null); }} onSubmit={handleSubmitForm} />
      <StaffConfirmDialog
        open={Boolean(confirm)}
        title={texts.title}
        message={texts.message}
        confirmLabel={texts.confirmLabel}
        color={texts.color}
        requireCheck={confirm?.kind === 'delete'}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirmed}
      />
      <StaffAuditDialog open={auditOpen} onClose={() => setAuditOpen(false)} />
    </Box>
  );
};

export default StaffPage;
